import { useQuery, useQueryClient, useMutation } from "react-query";
import { useState } from "react";
import { makeAuthedRequest } from "shared/utils/makeAuthedRequest";
import { useCallback } from "react";

export const SAMPLE_SORT_TYPES = {
  DateAsc: "date-asc",
  DateDesc: "date-desc",
  LikedAsc: "liked-asc",
  LikedDesc: "liked-desc",
};

export function useSamples({ contact }) {
  const queryClient = useQueryClient();
  const [sortBy, setSortBy] = useState(SAMPLE_SORT_TYPES.DateDesc);

  const { isLoading, isError, data, error } = useQuery(
    ["customers", "samples", contact.id, { sortBy }],
    () => {
      let path = `/customers/samples/${contact.id}`;

      const query = [["sortBy", sortBy]];

      path += "?";
      path += query
        .filter((pair) => pair)
        .map((pair) => `${pair[0]}=${encodeURIComponent(pair[1])}`)
        .join("&");

      return makeAuthedRequest(path).then(async (res) => {
        const json = await res.json();

        if (res.ok) {
          return json.data;
        } else {
          console.error(json);
          throw new Error("Request failed");
        }
      });
    }
  );

  const getTagSuggestions = useCallback((term) => {
    return makeAuthedRequest(`/customers/tags?searchTerm=${term}`)
      .then((res) => res.json())
      .then((json) => json.data);
  }, []);

  const addSampleMutation = useMutation(
    (sample) => makeAuthedRequest("/customers/samples", {
        method: "post",
        body: JSON.stringify({
          contactId: contact.id,
          tags: sample.tags,
          liked: sample.liked,
          note: sample.note,
          date: sample.date,
        }),
      }),
    {
      onMutate: async (sample) => {
        await queryClient.cancelQueries(["customers", "samples", contact.id]);

        const previousValue = queryClient.getQueryData(
          `/customers/samples/${contact.id}`
        );

        queryClient.setQueryData(
          ["customers", "samples", contact.id],
          (old) => [sample, ...(old || [])]
        );

        return previousValue;
      },
      onError: (err, vars, previousValue) => {
        queryClient.setQueryData(
          ["customers", "samples", contact.id],
          previousValue
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(["customers", "samples", contact.id]);
      },
    }
  );

  const updateSampleMutation = useMutation(
    (sample) =>
      makeAuthedRequest("/customers/samples", {
        method: "put",
        body: JSON.stringify({
          id: sample.id,
          contactId: contact.id,
          tags: sample.tags,
          liked: sample.liked,
          note: sample.note,
          date: sample.date,
        }),
      }),
    {
      onMutate: async (sample) => {
        await queryClient.cancelQueries(["customers", "samples", contact.id]);

        const previousValue = queryClient.getQueryData([
          "customers",
          "samples",
          contact.id,
        ]);

        // Replace sample with the same ID.
        queryClient.setQueryData(["customers", "samples", contact.id], (old) =>
          (old || []).map((s) => (s.id === sample.id ? sample : s))
        );

        return previousValue;
      },
      onError: (err, vars, previousValue) => {
        queryClient.setQueryData(
          ["customers", "samples", contact.id],
          previousValue
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(["customers", "samples", contact.id]);
      },
    }
  );

  const deleteSampleMutation = useMutation(
    (sampleId) =>
      makeAuthedRequest(`/customers/samples/${contact.id}/${sampleId}`, {
        method: "delete",
      }),
    {
      onMutate: async (sampleId) => {
        await queryClient.cancelQueries(["customers", "samples", contact.id]);

        const previousValue = queryClient.getQueryData([
          "customers",
          "samples",
          contact.id,
        ]);

        queryClient.setQueryData(["customers", "samples", contact.id], (old) =>
          (old || []).filter((s) => s.id !== sampleId)
        );

        return previousValue;
      },
      onError: (err, vars, previousValue) => {
        queryClient.setQueryData(
          ["customers", "samples", contact.id],
          previousValue
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries(["customers", "samples", contact.id]);
      },
    }
  );

  return {
    isLoading,
    isError,
    samples: data || [],
    error,

    sortBy,
    setSortBy,

    addSampleMutation,
    updateSampleMutation,
    deleteSampleMutation,

    getTagSuggestions,
  };
}
